import React, { useState, useMemo, useCallback, useEffect } from "react";
import "../registerPage/styles/index.less";
import {
  Alert,
  Button,
  Checkbox,
  Col,
  Form,
  Input,
  Row,
  Modal,
  message,
  Result,
  Typography,
  List,
  Tooltip,
  Collapse,
} from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import LogoBg from "../../assets/images/logo/benchmarklogo.png";
import LogoSign from "../../assets/images/bg/Benchmark.png";

import { user_create } from "../../services/user";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { useRecoilState } from "recoil";
import { languageState, localeState } from "../../utils/atoms";
import enUS from "antd/locale/en_US";
import thTH from "antd/locale/th_TH";

import { PolicyDetail, AgreementDetail } from "./policyData";
import { Content } from "antd/es/layout/layout";

const { Title, Text, Paragraph } = Typography;
const { Panel } = Collapse;

const TURNSTILE_SECRET_KEY = "0x4AAAAAACrtzSQKhSgv5Kq1"
const IS_DEVELOPMENT = process.env.NODE_ENV === "development";

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: string | HTMLElement,
        options: {
          sitekey: string;
          callback?: (token: string) => void;
          "expired-callback"?: () => void;
          "error-callback"?: () => void;
          theme?: "light" | "dark" | "auto";
          language?: string;
          appearance?: "always" | "execute" | "interaction-only";
        },
      ) => string;
      reset: (widgetId?: string) => void;
      remove: (widgetId?: string) => void;
    };
  }
}

interface FormValues {
  email: string;
  organization?: string;
  username: string;
  displayname: string;
  password: string;
  confirm_password: string;
  isCertificated?: boolean;
  sid?: string;
  firstname?: string;
  lastname?: string;
  agreement: boolean;
  turnstileToken?: string;
}

const PolicyListItem: React.FC<{ item: { no: string } }> = React.memo(
  ({ item }) => (
    <List.Item>
      <Row justify="center" align="middle" style={{ width: "100%" }}>
        <Col xs={24} sm={24} md={24} lg={24} xl={24}>
          <p
            style={{
              marginLeft: "40px",
              fontSize: "16px",
              lineHeight: "24px",
              wordBreak: "break-word",
            }}
          >
            {item.no}
          </p>
        </Col>
      </Row>
    </List.Item>
  ),
);

PolicyListItem.displayName = "PolicyListItem";

const PolicyModal: React.FC<{
  open: boolean;
  onClose: () => void;
}> = React.memo(({ open, onClose }) => (
  <Modal
    title="Privacy Policy"
    open={open}
    onCancel={onClose}
    width={500}
    centered
    footer={null}
  >
    <div style={{ maxHeight: "400px", overflowY: "auto" }}>
      <Paragraph className="detail-content" style={{ textIndent: "30px" }}>
        {PolicyDetail.topic}
      </Paragraph>

      <Paragraph className="detail-content">บทนำ</Paragraph>
      <Paragraph
        className="detail-content"
        style={{ marginLeft: "40px", textIndent: "30px" }}
      >
        {PolicyDetail.Introduction}
      </Paragraph>

      <Paragraph className="detail-content">คำจำกัดความ</Paragraph>
      <List
        dataSource={PolicyDetail.definition}
        renderItem={(item) => <PolicyListItem item={item} />}
      />

      <Paragraph className="detail-content">
        แหล่งที่มาของข้อมูลส่วนบุคคล
      </Paragraph>
      <List
        dataSource={PolicyDetail.personaldata}
        renderItem={(item) => <PolicyListItem item={item} />}
      />

      <Paragraph className="detail-content">
        ข้อมูลที่เราขอให้ผู้ใช้ให้แก่เรา
      </Paragraph>
      <List
        dataSource={PolicyDetail.giveus}
        renderItem={(item) => <PolicyListItem item={item} />}
      />

      <Paragraph className="detail-content">
        วัตถุประสงค์ในการเก็บรวบรวมข้อมูลส่วนบุคคล
      </Paragraph>
      <List
        dataSource={PolicyDetail.objective}
        renderItem={(item) => <PolicyListItem item={item} />}
      />

      <Paragraph className="detail-content">
        การประมวลผลข้อมูลส่วนบุคคล
      </Paragraph>
      <List
        dataSource={PolicyDetail.processing}
        renderItem={(item) => <PolicyListItem item={item} />}
      />

      <Paragraph className="detail-content">
        ระยะเวลาในการเก็บรักษาข้อมูลส่วนบุคคล
      </Paragraph>
      <List
        dataSource={PolicyDetail.period}
        renderItem={(item) => <PolicyListItem item={item} />}
      />

      <Paragraph className="detail-content">สิทธิของผู้ใช้</Paragraph>
      <List
        dataSource={PolicyDetail.rights}
        renderItem={(item) => <PolicyListItem item={item} />}
      />

      <Paragraph className="detail-content">
        ความปลอดภัยของข้อมูลส่วนบุคคล
      </Paragraph>
      <List
        dataSource={PolicyDetail.security}
        renderItem={(item) => <PolicyListItem item={item} />}
      />

      <Paragraph className="detail-content">
        การเปลี่ยนแปลงหรือปรับปรุงนโยบายคุ้มครองข้อมูลส่วนบุคคลนี้
      </Paragraph>
      <List
        dataSource={PolicyDetail.change}
        renderItem={(item) => <PolicyListItem item={item} />}
      />

      <Paragraph className="detail-content">
        การปฏิบัติตามนโยบายคุ้มครองข้อมูลส่วนบุคคลและการติดต่อกับเรา
      </Paragraph>
      <List
        dataSource={PolicyDetail.policy}
        renderItem={(item) => <PolicyListItem item={item} />}
      />
    </div>
  </Modal>
));

PolicyModal.displayName = "PolicyModal";

const AgreementModal: React.FC<{
  open: boolean;
  onClose: () => void;
}> = React.memo(({ open, onClose }) => (
  <Modal
    title="User Agreement"
    open={open}
    onCancel={onClose}
    width={500}
    centered
    footer={null}
  >
    <div style={{ maxHeight: "400px", overflowY: "auto" }}>
      <Paragraph className="detail-content" style={{ textIndent: "30px" }}>
        {AgreementDetail.topic}
      </Paragraph>

      <Paragraph className="detail-content">คำจำกัดความ</Paragraph>
      <List
        dataSource={AgreementDetail.definition}
        renderItem={(item) => <PolicyListItem item={item} />}
      />

      <Paragraph className="detail-content">
        ข้อกำหนดการใช้งานแพลตฟอร์ม
      </Paragraph>
      <Paragraph className="detail-content">
        การเข้าถึงและใช้งานแพลตฟอร์มถือว่าผู้ใช้ยอมรับและตกลงที่จะปฏิบัติตามข้อตกลงการใช้นี้
      </Paragraph>
      <List
        dataSource={AgreementDetail.regulation}
        renderItem={(item) => <PolicyListItem item={item} />}
      />
    </div>
  </Modal>
));

AgreementModal.displayName = "AgreementModal";

const RegisterPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [form] = Form.useForm();

  const [activeLanguage, setActiveLanguage] = useState("enUS");
  const { i18n } = useTranslation();

  const [language, setLanguage] = useRecoilState(languageState);
  const [locale, setLocale] = useRecoilState(localeState);

  const [loading, setLoading] = useState(false);
  const [isRegisterSuccess, setIsRegisterSuccess] = useState(false);
  const [isPolicyModalOpen, setIsPolicyModalOpen] = useState(false);
  const [isUserAgreementModalOpen, setIsUserAgreementModalOpen] =
    useState(false);
  const [isCertificated, setIsCertificated] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [messageApi, contextHolder] = message.useMessage();
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileWidgetId, setTurnstileWidgetId] = useState<string | null>(
    null,
  );

  const handleCertificatedCheckboxChange = useCallback(
    (e: any) => {
      const checked = e.target.checked;
      setIsCertificated(checked);

      if (!checked) {
        form.setFieldsValue({
          sid: undefined,
          firstname: undefined,
          lastname: undefined,
        });
      }
    },
    [form],
  );

  const handleTurnstileChange = useCallback(
    (token: string) => {
      setTurnstileToken(token);
      form.setFieldsValue({ turnstileToken: token });
    },
    [form],
  );

  const normalizeUsernameInput = useCallback((value: string) => {
    return value.toLowerCase().replace(/[^a-z0-9._-]/g, "");
  }, []);

  const resetTurnstile = useCallback(() => {
    setTurnstileToken(null);
    form.setFieldsValue({ turnstileToken: undefined });

    if (window.turnstile && turnstileWidgetId) {
      window.turnstile.reset(turnstileWidgetId);
    }
  }, [form, turnstileWidgetId]);

  const validatePassword = useCallback((_: any, value: string) => {
    if (!value) {
      return Promise.reject(new Error("กรุณากรอกรหัสผ่าน"));
    }
    if (value.length < 8) {
      return Promise.reject(new Error("รหัสผ่านต้องมีอย่างน้อย 8 ตัวอักษร"));
    }

    const hasUpperCase = /[A-Z]/.test(value);
    const hasLowerCase = /[a-z]/.test(value);
    const hasNumber = /\d/.test(value);

    if (!hasUpperCase || !hasLowerCase || !hasNumber) {
      return Promise.reject(
        new Error("รหัสผ่านต้องมีตัวพิมพ์ใหญ่ พิมพ์เล็ก และตัวเลข"),
      );
    }

    return Promise.resolve();
  }, []);

  const validateConfirmPassword = useCallback(
    (_: any, value: string) => {
      const password = form.getFieldValue("password");
      if (!value) {
        return Promise.reject(new Error("กรุณายืนยันรหัสผ่าน"));
      }
      if (value !== password) {
        return Promise.reject(new Error("รหัสผ่านไม่ตรงกัน"));
      }
      return Promise.resolve();
    },
    [form],
  );

  const showMessage = useCallback(
    (
      type: "success" | "error" | "warning",
      content: string,
      onClose?: () => void,
    ) => {
      messageApi.open({ type, content, onClose });
    },
    [messageApi],
  );

  useEffect(() => {
    if (IS_DEVELOPMENT || !TURNSTILE_SECRET_KEY) {
      return;
    }

    let isMounted = true;
    let script = document.querySelector<HTMLScriptElement>(
      'script[src="https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit"]',
    );

    const renderTurnstile = () => {
      if (
        !isMounted ||
        !window.turnstile ||
        turnstileWidgetId ||
        !document.getElementById("cf-turnstile-widget")
      ) {
        return;
      }

      const widgetId = window.turnstile.render("#cf-turnstile-widget", {
        sitekey: TURNSTILE_SECRET_KEY,
        callback: (token: string) => {
          handleTurnstileChange(token);
        },
        "expired-callback": () => {
          resetTurnstile();
        },
        "error-callback": () => {
          resetTurnstile();
          showMessage("error", "❌ Cloudflare Turnstile ทำงานผิดพลาด");
        },
        theme: "light",
        language: activeLanguage === "thTH" ? "th" : "en",
        appearance: "always",
      });

      setTurnstileWidgetId(widgetId);
    };

    if (!script) {
      script = document.createElement("script");
      script.src =
        "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      script.async = true;
      script.defer = true;
      script.onload = renderTurnstile;
      document.head.appendChild(script);
    } else if (window.turnstile) {
      renderTurnstile();
    } else {
      script.addEventListener("load", renderTurnstile);
    }

    return () => {
      isMounted = false;
      if (script) {
        script.removeEventListener("load", renderTurnstile);
      }
    };
  }, [
    activeLanguage,
    form,
    handleTurnstileChange,
    resetTurnstile,
    showMessage,
    turnstileWidgetId,
  ]);

  useEffect(() => {
    return () => {
      if (window.turnstile && turnstileWidgetId) {
        window.turnstile.remove(turnstileWidgetId);
      }
    };
  }, [turnstileWidgetId]);

  const handleCreateUser = useCallback(
    async (values: FormValues) => {
      try {
        if (!IS_DEVELOPMENT && !turnstileToken) {
          showMessage(
            "warning",
            "⚠️ กรุณายืนยันตัวตนผ่าน Cloudflare Turnstile ก่อนลงทะเบียน",
          );
          return;
        }

        setLoading(true);

        const userData: any = {
          name: values.displayname.trim(),
          username: normalizeUsernameInput(values.username.trim()),
          email: values.email.trim(),
          password: values.password,
          org_id: "e82fb7f3-3fa0-446d-8ef4-9743421b4ca8",
          org: values.organization?.trim() || "",
          role_id: "ecbf4355-952b-4cc2-8136-8353ca55b0d2",
          url: "/user/register/confirm",
          turnstile_token: turnstileToken,
          "cf-turnstile-response": turnstileToken,
        };

        if (isCertificated) {
          if (values.sid && values.firstname && values.lastname) {
            userData.citizen_id = values.sid.trim();
            userData.first_name = values.firstname.trim();
            userData.last_name = values.lastname.trim();
          } else {
            showMessage(
              "warning",
              "⚠️ กรุณากรอกข้อมูล SID, Firstname และ Lastname",
            );
            setLoading(false);
            return;
          }
        }

        console.log("📤 Sending data to API:", userData);

        const res = await user_create({
          data: userData,
        });

        if (res.message === "success") {
          setUserEmail(values.email);
          setIsRegisterSuccess(true);
          showMessage("success", "✅ ลงทะเบียนสำเร็จ!");

          form.resetFields();
          resetTurnstile();
          setIsCertificated(false);
        } else if (res.message === "duplicate email") {
          resetTurnstile();
          showMessage("warning", "❗ มีผู้ใช้งาน Email นี้ในระบบแล้ว");
        } else if (res.message === "duplicate username") {
          resetTurnstile();
          showMessage("warning", "❗ มีผู้ใช้งานนี้ในระบบแล้ว");
        } else {
          resetTurnstile();
          showMessage("error", "❌ ลงทะเบียนไม่สำเร็จ กรุณาลองใหม่อีกครั้ง");
        }
      } catch (e) {
        console.error("🚨 Error creating user:", e);
        resetTurnstile();
        showMessage("error", "❌ เกิดข้อผิดพลาดในการลงทะเบียน");
      } finally {
        setLoading(false);
      }
    },
    [
      isCertificated,
      form,
      IS_DEVELOPMENT,
      normalizeUsernameInput,
      resetTurnstile,
      showMessage,
      turnstileToken,
    ],
  );

  const onFinishFailed = useCallback(
    (errorInfo: any) => {
      console.error("Form validation failed:", errorInfo);
      showMessage("warning", "กรุณากรอกข้อมูลให้ครบถ้วนและถูกต้อง");
    },
    [showMessage],
  );

  const handleCancel = useCallback(() => {
    navigate("/");
    // navigate("/login");
  }, [navigate]);

  const handleBackToLogin = useCallback(() => {
    setIsRegisterSuccess(false);
    navigate("/");
    // navigate("/login");
  }, [navigate]);

  const openPolicyModal = useCallback(() => setIsPolicyModalOpen(true), []);
  const closePolicyModal = useCallback(() => setIsPolicyModalOpen(false), []);
  const openAgreementModal = useCallback(
    () => setIsUserAgreementModalOpen(true),
    [],
  );
  const closeAgreementModal = useCallback(
    () => setIsUserAgreementModalOpen(false),
    [],
  );

  const onChangeLanguage = (lang: any) => {
    if (lang === "thTH") {
      i18n.changeLanguage("thTH");
      setLocale(thTH);
      setLanguage("thTH");
      localStorage.setItem("locale", "thTH");
    } else {
      i18n.changeLanguage("enUS");
      setLocale(enUS);
      setLanguage("enUS");
      localStorage.setItem("locale", "enUS");
    }
    setActiveLanguage(lang);
    console.log("lang", lang);
  };

  useEffect(() => {
    const storedLocale = localStorage.getItem("locale") || "enUS";
    if (storedLocale === "thTH") {
      i18n.changeLanguage("thTH");
      setLocale(thTH);
      setLanguage("thTH");
      setActiveLanguage("thTH");
    } else {
      i18n.changeLanguage("enUS");
      setLocale(enUS);
      setLanguage("enUS");
      setActiveLanguage("enUS");
    }
  }, [i18n, setLocale, setLanguage]);

  const LanguageSwitcher = useMemo(
    () => (
      <div style={{ position: "absolute", top: 20, left: 20 }}>
        <Row
          className="w-100 h-100"
          justify="space-between"
          align="middle"
          gutter={16}
          style={{ height: "100%" }}
        >
          <Col xs={0} sm={0} md={8} lg={8}>
            <Row align="middle" gutter={16}>
              <Col>
                <span
                  onClick={() => onChangeLanguage("thTH")}
                  style={{
                    color: activeLanguage === "thTH" ? "#BC224A" : "black",
                    fontWeight: "bold",
                    fontSize: "20px",
                    cursor: "pointer",
                  }}
                >
                  TH
                </span>
                <span style={{ margin: "0 8px", fontSize: "20px" }}>|</span>
                <span
                  onClick={() => onChangeLanguage("enUS")}
                  style={{
                    color: activeLanguage === "enUS" ? "#BC224A" : "black",
                    fontWeight: "bold",
                    fontSize: "20px",
                    cursor: "pointer",
                  }}
                >
                  EN
                </span>
              </Col>
            </Row>
          </Col>
        </Row>
      </div>
    ),
    [activeLanguage],
  );

  if (isRegisterSuccess) {
    return (
      <div className="register-container">
        <Result
          status="success"
          title="ลงทะเบียนสำเร็จ!"
          subTitle={`คุณสามารถตรวจสอบอีเมลที่ ${userEmail} เพื่อยืนยันบัญชี`}
          extra={[
            <Button type="primary" key="login" onClick={handleBackToLogin}>
              เข้าสู่ระบบ
            </Button>,
          ]}
        />
      </div>
    );
  }

  return (
    <Content>
      <div className="register-container">
        {contextHolder}

        <Row justify="center" align="middle">
          <Col xs={22} sm={22} md={22} lg={20} xl={18} xxl={16}>
            <div className="register-card">
              {LanguageSwitcher}
              <Row gutter={[24, 24]} justify="center" align="middle">
                <Col xs={0} sm={0} md={12} lg={12} xl={12}>
                  <div className="register-illustration">
                    <img
                      src={LogoSign}
                      alt="Register Illustration"
                      style={{ width: "100%", maxWidth: "auto" }}
                    />
                  </div>
                </Col>

                <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                  <div className="register-form-container">
                    <div style={{ textAlign: "center", marginBottom: "24px" }}>
                      <img
                        src={LogoBg}
                        alt="AI Model Benchmark"
                        style={{ height: "60px" }}
                      />
                      <Title level={3} style={{ marginTop: "16px" }}>
                        {t("Sign Up")}
                      </Title>
                    </div>

                    <Form
                      form={form}
                      layout="vertical"
                      onFinish={handleCreateUser}
                      onFinishFailed={onFinishFailed}
                      autoComplete="off"
                      requiredMark={false}
                    >
                      <Form.Item
                        label={t("Email")}
                        name="email"
                        rules={[
                          { required: true, message: "กรุณากรอกอีเมล" },
                          { type: "email", message: "รูปแบบอีเมลไม่ถูกต้อง" },
                        ]}
                      >
                        <Input placeholder="Your Email" />
                      </Form.Item>

                      <Form.Item label={t("organization")} name="organization">
                        <Input placeholder="Your Organization" />
                      </Form.Item>

                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item
                            label={t("Username")}
                            name="username"
                            rules={[
                              { required: true, message: "กรุณากรอก Username" },
                              {
                                min: 3,
                                message: "Username ต้องมีอย่างน้อย 3 ตัวอักษร",
                              },
                              {
                                max: 20,
                                message: "Username ต้องมีความยาวไม่เกิน 20 ตัวอักษร",
                              },
                              {
                                pattern: /^[a-z0-9._-]{3,20}$/,
                                message:
                                  "Username ใช้ได้เฉพาะ a-z, 0-9, จุด (.), ขีดล่าง (_) และขีดกลาง (-)",
                              },
                            ]}
                          >
                            <Input
                              placeholder="your.username"
                              maxLength={20}
                              onChange={(e) => {
                                const normalizedValue = normalizeUsernameInput(
                                  e.target.value,
                                );
                                if (normalizedValue !== e.target.value) {
                                  form.setFieldsValue({
                                    username: normalizedValue,
                                  });
                                }
                              }}
                            />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            label={t("Display Name")}
                            name="displayname"
                            rules={[
                              {
                                required: true,
                                message: "กรุณากรอก Display Name",
                              },
                            ]}
                          >
                            <Input placeholder="Your Displayname" />
                          </Form.Item>
                        </Col>
                      </Row>

                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item
                            label={t("password")}
                            name="password"
                            rules={[{ validator: validatePassword }]}
                          >
                            <Input.Password placeholder="Your Password" />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            label={t("confirm password")}
                            name="confirm_password"
                            dependencies={["password"]}
                            rules={[{ validator: validateConfirmPassword }]}
                          >
                            <Input.Password placeholder="Confirm Password" />
                          </Form.Item>
                        </Col>
                      </Row>

                      <Form.Item
                        name="isCertificated"
                        valuePropName="checked"
                        style={{ marginBottom: "8px" }}
                      >
                        <Collapse defaultActiveKey={["1"]}>
                          <Panel
                            header={t("Register With Certificated")}
                            key="1"
                            extra={
                              <Checkbox
                                checked={isCertificated}
                                onChange={handleCertificatedCheckboxChange}
                                onClick={(e) => {
                                  e.stopPropagation();
                                }}
                              ></Checkbox>
                            }
                          >
                            <div>
                              <Form.Item
                                label={t("ID")}
                                name="sid"
                                rules={[
                                  {
                                    required: isCertificated,
                                    message: "กรุณากรอก ID",
                                  },
                                  {
                                    validator: (_, value) => {
                                      if (!isCertificated || !value) {
                                        return Promise.resolve();
                                      }

                                      if (!/^[0-9]+$/.test(value)) {
                                        return Promise.reject(
                                          new Error(
                                            "ID ต้องเป็นตัวเลขเท่านั้น",
                                          ),
                                        );
                                      }

                                      if (value.length !== 13) {
                                        return Promise.reject(
                                          new Error(
                                            "ID ต้องมีความยาว 13 ตัวพอดี",
                                          ),
                                        );
                                      }

                                      return Promise.resolve();
                                    },
                                  },
                                ]}
                              >
                                <Input
                                  placeholder="Your ID (13 digits)"
                                  disabled={!isCertificated}
                                  maxLength={13}
                                  onKeyPress={(e) => {
                                    if (!/[0-9]/.test(e.key)) {
                                      e.preventDefault();
                                    }
                                  }}
                                />
                              </Form.Item>

                              <Row gutter={16}>
                                <Col span={12}>
                                  <Form.Item
                                    label={t("Firstname")}
                                    name="firstname"
                                    rules={[
                                      {
                                        required: isCertificated,
                                        message: "กรุณากรอกชื่อจริง",
                                      },
                                    ]}
                                  >
                                    <Input
                                      placeholder="Your Firstname"
                                      disabled={!isCertificated}
                                    />
                                  </Form.Item>
                                </Col>
                                <Col span={12}>
                                  <Form.Item
                                    label={t("Lastname")}
                                    name="lastname"
                                    rules={[
                                      {
                                        required: isCertificated,
                                        message: "กรุณากรอกนามสกุล",
                                      },
                                    ]}
                                  >
                                    <Input
                                      placeholder="Your Lastname"
                                      disabled={!isCertificated}
                                    />
                                  </Form.Item>
                                </Col>
                              </Row>

                              <Text
                                type="secondary"
                                style={{ fontSize: "12px" }}
                              >
                                <InfoCircleOutlined /> {t("Note Register")}
                              </Text>
                            </div>
                          </Panel>
                        </Collapse>
                      </Form.Item>

                      <Form.Item
                        name="turnstileToken"
                        rules={[
                          {
                            validator: () =>
                              turnstileToken
                                ? Promise.resolve()
                                : IS_DEVELOPMENT
                                ? Promise.resolve()
                                : Promise.reject(
                                    new Error(
                                      "กรุณายืนยันตัวตนผ่าน Cloudflare Turnstile",
                                    ),
                                  ),
                          },
                        ]}
                      >
                        <div
                          style={{
                            marginBottom: 12,
                            padding: "16px",
                            borderRadius: "16px",
                            border: "1px solid #d9e2f2",
                            background:
                              "linear-gradient(135deg, #f8fbff 0%, #eef4ff 100%)",
                            boxShadow: "0 8px 24px rgba(14, 33, 160, 0.08)",
                          }}
                        >
                          <div style={{ marginBottom: 10 }}>
                            <Text
                              strong
                              style={{ fontSize: "14px", color: "#0E21A0" }}
                            >
                              Cloudflare Turnstile Verification
                            </Text>
                            <br />
                            <Text type="secondary" style={{ fontSize: "12px" }}>
                              กรุณายืนยันว่าคุณไม่ใช่บอท
                            </Text>
                          </div>

                          {IS_DEVELOPMENT ? (
                            <Alert
                              type="info"
                              showIcon
                              message="Development mode"
                              description="ข้ามการตรวจสอบ Cloudflare Turnstile ในโหมดพัฒนา"
                            />
                          ) : !TURNSTILE_SECRET_KEY ? (
                            <Alert
                              type="warning"
                              showIcon
                              message="ยังไม่ได้ตั้งค่า Turnstile site key"
                              description="กรุณากำหนด REACT_APP_CLOUDFLARE_TURNSTILE_SITE_KEY ในไฟล์ environment"
                            />
                          ) : (
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "center",
                                minHeight: "70px",
                              }}
                            >
                              <div id="cf-turnstile-widget" />
                            </div>
                          )}
                        </div>
                      </Form.Item>

                      <Form.Item
                        name="agreement"
                        valuePropName="checked"
                        rules={[
                          {
                            validator: (_, value) =>
                              value
                                ? Promise.resolve()
                                : Promise.reject(
                                    new Error("กรุณายอมรับข้อตกลงและนโยบาย"),
                                  ),
                          },
                        ]}
                      >
                        <Checkbox>
                          {t("Agree to the")}{" "}
                          <a
                            onClick={(e) => {
                              e.preventDefault();
                              openPolicyModal();
                            }}
                          >
                            {t("Website Privacy Policy")}
                          </a>
                          {" และ "}
                          <a
                            onClick={(e) => {
                              e.preventDefault();
                              openAgreementModal();
                            }}
                          >
                            {t("Terms of Use")}
                          </a>
                        </Checkbox>
                      </Form.Item>

                      <Form.Item>
                        <Row className="button-group" justify="space-between">
                          <Col>
                            <Button
                              type="default"
                              htmlType="button"
                              className="sign-up-form-button"
                              onClick={() => {
                                window.scrollTo(0, 0);
                                navigate("/");
                              }}
                            >
                              {t("cancel")}
                            </Button>
                          </Col>
                          <Col>
                            <Button
                              type="primary"
                              style={{
                                backgroundColor: "#0E21A0",
                                color: "white",
                              }}
                              htmlType="submit"
                              className="sign-up-form-button"
                              loading={loading}
                            >
                              {t("Register Now")}
                            </Button>
                          </Col>
                        </Row>
                      </Form.Item>
                    </Form>
                  </div>
                </Col>
              </Row>
            </div>
          </Col>
        </Row>

        <PolicyModal open={isPolicyModalOpen} onClose={closePolicyModal} />
        <AgreementModal
          open={isUserAgreementModalOpen}
          onClose={closeAgreementModal}
        />
      </div>
    </Content>
  );
};

export default RegisterPage;
